/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { TaskRepresentation } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class TaskActionsApi extends BaseApi {
    assignTask(taskId, userIdentifier) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(userIdentifier, 'userIdentifier');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/assign',
            pathParams,
            bodyParam: userIdentifier,
            returnType: TaskRepresentation
        });
    }
    attachForm(taskId, formIdentifier) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(formIdentifier, 'formIdentifier');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/attach-form',
            pathParams,
            bodyParam: formIdentifier
        });
    }
    claimTask(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/claim',
            pathParams
        });
    }
    completeTask(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/complete',
            pathParams
        });
    }
    delegateTask(taskId, userIdentifier) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(userIdentifier, 'userIdentifier');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/delegate',
            pathParams,
            bodyParam: userIdentifier
        });
    }
    involveGroup(taskId, groupId) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            taskId,
            groupId
        };
        return this.post({
            path: '/api/enterprise/tasks/{taskId}/groups/{groupId}',
            pathParams
        });
    }
    involveUser(taskId, userIdentifier) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(userIdentifier, 'userIdentifier');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/involve',
            pathParams,
            bodyParam: userIdentifier
        });
    }
    removeForm(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.delete({
            path: '/api/enterprise/tasks/{taskId}/action/remove-form',
            pathParams
        });
    }
    removeInvolvedUser(taskId, identifier) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(identifier, 'identifier');
        const pathParams = {
            taskId,
            identifier
        };
        if (identifier instanceof String) {
            return this.delete({
                path: '/api/enterprise/tasks/{taskId}/groups/{groupId}',
                pathParams
            });
        }
        else {
            return this.put({
                path: '/api/enterprise/tasks/{taskId}/action/remove-involved',
                pathParams,
                bodyParam: identifier
            });
        }
    }
    resolveTask(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/resolve',
            pathParams
        });
    }
    unclaimTask(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}/action/unclaim',
            pathParams
        });
    }
}
